import React, { useState, useEffect } from 'react';
import { Users, Search, Database, Bookmark, TrendingUp, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../utils/api';

const COLORS = ['#e11d48','#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data.stats)).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div className="empty-state"><div className="spinner spinner-lg"/></div>;
  if (!stats) return <div className="alert alert-error">Failed to load stats</div>;

  const STAT_CARDS = [
    { label:'Total Users', value:stats.totalUsers, icon:<Users size={20}/>, color:'red', change:'+' },
    { label:'Active Users', value:stats.activeUsers, icon:<Activity size={20}/>, color:'green', change:'' },
    { label:'Total Searches', value:stats.totalSearches, icon:<Search size={20}/>, color:'blue', change:'' },
    { label:'Leads in DB', value:stats.totalLeads?.toLocaleString(), icon:<Database size={20}/>, color:'purple', change:'' },
    { label:'Saved Leads', value:stats.savedLeads, icon:<Bookmark size={20}/>, color:'gold', change:'' },
    { label:'Searches Today', value:stats.todaySearches, icon:<TrendingUp size={20}/>, color:'green', change:'' },
  ];

  return (
    <div>
      <div className="admin-page-title">Platform Overview</div>
      <div className="admin-page-sub">Real-time stats across all users and searches</div>

      {/* Stat Cards */}
      <div className="grid-stats mb-24">
        {STAT_CARDS.map((s,i) => (
          <div key={i} className="stat-card">
            <div className={`stat-icon ${s.color}`}>{s.icon}</div>
            <div>
              <div className="stat-num">{s.value ?? '—'}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid-2 mb-24">
        {/* Daily Searches Chart */}
        <div className="card">
          <div className="card-header"><div className="card-title">Daily Searches (30 days)</div></div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={stats.dailySearches||[]}>
              <XAxis dataKey="date" tick={{fontSize:10}} tickFormatter={v=>v.slice(5)}/>
              <YAxis tick={{fontSize:10}}/>
              <Tooltip labelFormatter={v=>`Date: ${v}`} formatter={v=>[v,'Searches']}/>
              <Line type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Industry Breakdown */}
        <div className="card">
          <div className="card-header"><div className="card-title">Users by Industry</div></div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={stats.industryBreakdown||[]} dataKey="count" nameKey="industry" cx="50%" cy="50%" outerRadius={80}>
                {(stats.industryBreakdown||[]).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip formatter={(v,n)=>[v,n]}/>
              <Legend iconSize={10} iconType="circle" formatter={v=>v.length>14?v.slice(0,14)+'…':v}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Searchers */}
      <div className="card">
        <div className="card-header"><div className="card-title">Top Active Users</div></div>
        <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
          <table className="table">
            <thead>
              <tr><th>User</th><th>Company</th><th>Email</th><th>Searches</th></tr>
            </thead>
            <tbody>
              {(stats.topSearchers||[]).map((u,i) => (
                <tr key={i}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                      <div style={{ width:30, height:30, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <span style={{ fontWeight:600, fontSize:13.5 }}>{u.first_name} {u.last_name}</span>
                    </div>
                  </td>
                  <td>{u.company||'—'}</td>
                  <td style={{ fontSize:12.5, color:'var(--text3)' }}>{u.email}</td>
                  <td><span style={{ fontWeight:700, color:'var(--accent)', fontSize:15 }}>{u.search_count}</span></td>
                </tr>
              ))}
              {!stats.topSearchers?.length && <tr><td colSpan={4} style={{textAlign:'center',color:'var(--text3)',padding:24}}>No data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
